import React, { useEffect, type MouseEvent, type SyntheticEvent } from "react";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import clsx from "clsx";

import { Tabs, Tab, IconButton, Box, Typography, Drawer, styled, Tooltip } from "@mui/material";

import menuIcon from "@user/assets/images/common/menu.svg";
import favoriteIcon from "@user/assets/images/common/star.svg";
import homeIcon from "@user/assets/images/common/home.svg";
import closeIcon from "@user/assets/images/common/close.svg";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";

import Header from "@user/layout/header";
import Left from "@user/layout/left";

import variables from "@user/assets/css/_variables.module.scss";

const Main = styled("div", { shouldForwardProp: (prop) => prop !== "showLeft" })<{
  showLeft?: boolean;
}>(({ theme }) => ({
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${variables.widthLeftArea}`,
  variants: [
    {
      props: ({ showLeft }) => showLeft,
      style: {
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
      },
    },
  ],
}));

export default function TabsLayout(_props: any) {
  const MAIN_TAB_ID = "/";
  const [showLeft, setShowLeft] = React.useState(false);
  const tabs = useTabsStore((state) => state.tabs);
  const currTabId = useTabsStore((state) => state.currTabId);
  const isFavorite = useTabsStore((state) => state.getCurrTab()?.isFavorite);

  useEffect(() => {
  }, []);

  useEffect(() => {
    console.log("TABS CHANGED[" + currTabId + "]");
  }, [tabs]);

  function toggleFavorite() {
    if (currTabId == MAIN_TAB_ID) {
      return;
    }
    useTabsStore.getState().toggleFavorite();
  }

  function removeTab(id: string) {
    useTabsStore.getState().removeTab(id);
  }

  function onChangeTab(_event: SyntheticEvent, value: any) {
    useTabsStore.getState().changeTab(value);
  }

  return (
    <Box sx={{ display: "flex" }}>
      <Header />
      <Drawer className="left-area" variant="persistent" anchor="left" open={showLeft}>
        <Left onClose={() => setShowLeft(false)} />
      </Drawer>
      <Main className="main-area" showLeft={showLeft}>
        <div className="main-area-sub">
          <div className="menu-tab">
            {
              !showLeft &&
              <Tooltip title="메뉴">
                  <button className="icon-button" onClick={() => setShowLeft(true)}><img src={menuIcon} alt="메뉴" /></button>
              </Tooltip>
            }
            <Tooltip title={`즐겨찾기 ${isFavorite ? "삭제" : "추가"}`}>
              <button className={clsx("icon-button", currTabId != MAIN_TAB_ID && isFavorite && "on")} onClick={() => toggleFavorite()}><img src={favoriteIcon} alt="즐겨찾기" /></button>
            </Tooltip>
            <Tooltip title="홈">
              <button className={clsx("icon-button", currTabId === MAIN_TAB_ID && "on")} onClick={() => router.navigateByPath("/")}><img src={homeIcon} alt="홈" /></button>
            </Tooltip>
            <Tabs
              value={currTabId !== MAIN_TAB_ID ? currTabId : false}
              onChange={onChangeTab}
              variant="scrollable"
              scrollButtons="auto"
              slotProps={{ scrollButtons: {
                disableRipple: true,
              }}}
            >
              {tabs.map((tab: any) => (
                tab.id != MAIN_TAB_ID &&
                <Tab
                  key={tab.id}
                  value={tab.id}
                  disableRipple
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2">{tab.label}</Typography>
                      {!tab.isFavorite && 
                        <Tooltip title="닫기">
                          <IconButton
                            size="small"
                            component="span"
                            onClick={(_e) => removeTab(tab.id)}
                          >
                            <img src={closeIcon} style={{ width: 16, height: 16}} />
                          </IconButton>
                        </Tooltip>
                      }
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </div>
          <div>
            {tabs.map((tab: any) => (
              <div key={tab.path} style={{ display: currTabId === tab.id ? "block" : "none" }}>
                { tab.isInit &&
                  <TabComponent id={tab.id} Component={tab.component} />
                }
              </div>
            ))}
          </div>
        </div>
      </Main>
    </Box>
  )
}

function Fallback({ error }: FallbackProps) {
  const message = error instanceof Error ? error.message : String(error);

  return (
    <div role="alert">
      <p>렌더링 오류가 발생했습니다.</p>
      <pre>{message}</pre>
    </div>
  );
}

const TabComponent = ({ Component, id }: {
  Component: React.ComponentType<{ id: string }>;
  id: string;
}) => {
  try {
    return (
      <ErrorBoundary FallbackComponent={Fallback}>
        <Component id={id} />
      </ErrorBoundary>
    )
  } catch(e) {
    console.log(e);
  }
}
